import { Autocomplete, Checkbox, Stack, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { TextField } from '@side/pantry';
import { useMemo, useState } from 'react';
import { DropdownLoadingIcon } from '@/components/DropdownLoadingIcon';
import type { InteractionTypeFilter } from '@/views/Interactions/state/filters';
import {
  TrackInteractionsEvent,
  useTrackInteractionsAnalytics,
} from '$analytics/useTrackAnalytics/domains/interactions';
import { createFilterAutocompleteAlertPaper } from './FilterAutocompleteAlertPaper';
import { filterOptions, getCheckboxState, getOptionsToToggle } from './helpers';
import type { FilterOption } from './types';

interface Props {
  type: InteractionTypeFilter;
  filterType: string;
  eventContainer: string;
  label?: string;
  hideLabel?: boolean;
  placeholder?: string;
  value: FilterOption[];
  options: FilterOption[];
  /** Initial option load. Disables the field, since there is nothing to filter yet. */
  isLoadingOptions?: boolean;
  /**
   * A server-side search is in flight. Shows the loading affordance but leaves
   * the field editable — disabling it would interrupt the typing that caused it.
   */
  isSearching?: boolean;
  /** Renders an informational banner above the option list. */
  alertMessage?: string;
  /** Lifts the search term out of local state, for server-side filtering. */
  inputValue?: string;
  onInputChange?: (value: string) => void;
  onClear?: () => void;
  onToggle?: (value: FilterOption) => void;
}

export function FilterAutocomplete({
  type,
  filterType,
  eventContainer,
  label,
  hideLabel,
  placeholder = 'Search or select',
  value,
  options,
  isLoadingOptions,
  isSearching,
  alertMessage,
  inputValue: inputValueProp,
  onInputChange,
  onClear,
  onToggle,
}: Props) {
  const trackEvent = useTrackInteractionsAnalytics();
  const [internalInputValue, setInternalInputValue] = useState('');
  const inputValue = inputValueProp ?? internalInputValue;
  const setInputValue = onInputChange ?? setInternalInputValue;

  // Memoized so a re-render doesn't remount the open popup.
  const alertPaper = useMemo(
    () =>
      alertMessage
        ? createFilterAutocompleteAlertPaper(alertMessage)
        : undefined,
    [alertMessage],
  );

  const handleClear = () => {
    trackEvent(TrackInteractionsEvent.ClearFilterAutocomplete, {
      type,
      filterType,
      eventContainer,
    });

    onClear?.();
  };

  const selectedValues = new Set(value.map((option) => option.value));

  const handleToggle = (option: FilterOption) => {
    const { checked: isSelected } = getCheckboxState(option, selectedValues);
    const [_prefix, ...idParts] = option.value.split(':');
    const filterId = idParts.join(':');

    trackEvent(TrackInteractionsEvent.ToggleFilterOption, {
      type,
      filterType,
      eventContainer,
      filterName: option.label,
      filterId,
      searchString: inputValue,
      task: isSelected ? 'deselect' : 'select',
    });

    const optionsToToggle = getOptionsToToggle(
      option,
      isSelected,
      selectedValues,
    );

    for (const optionToToggle of optionsToToggle) {
      onToggle?.(optionToToggle);
    }
  };

  return (
    <Autocomplete
      id={filterType}
      autoComplete
      disableCloseOnSelect
      disablePortal
      disabled={isLoadingOptions}
      getOptionKey={(option) => option.value}
      inputValue={inputValue}
      slots={alertPaper ? { paper: alertPaper } : undefined}
      slotProps={{
        listbox: {
          sx: {
            '& .MuiAutocomplete-option': {
              fontSize: 14,
            },
          },
        },
        popper: {
          sx: {
            '& .MuiAutocomplete-paper': {
              maxHeight: 400,
              overflowY: 'auto',
            },
          },
        },
      }}
      multiple
      onChange={(_, _newValue, reason, details) => {
        if (reason === 'clear') {
          handleClear();
        } else if (reason === 'selectOption' || reason === 'removeOption') {
          if (details) {
            handleToggle(details.option);
          }
        }
      }}
      onInputChange={(_, v) => {
        setInputValue(v);
      }}
      options={options}
      isOptionEqualToValue={(option, candidate) =>
        option.value === candidate?.value
      }
      popupIcon={
        isLoadingOptions || isSearching ? <DropdownLoadingIcon /> : undefined
      }
      renderInput={(params) => (
        <TextField
          {...params}
          slotProps={{
            ...params.slotProps,
            inputLabel: {
              ...params.slotProps.inputLabel,
              shrink: false,
              sx: {
                display: inputValue.length ? 'none' : 'unset',
                fontSize: 16,
                height: 22,
                padding: 0,
                transform: 'translate(14px, 10px) scale(1) !important',
                '&.Mui-focused': {
                  display: 'none',
                },
              },
            },
          }}
          LabelProps={{
            sx: hideLabel ? { ...visuallyHidden } : {},
          }}
          label={label}
          placeholder={placeholder}
          sx={{
            '&.MuiAutocomplete-root.MuiAutocomplete-input': {
              py: '0px !important',
            },
            '& .MuiInputLabel-root': {
              fontSize: '16px !important',
              height: '22px !important',
              transform: 'translate(14px, 12px) scale(1)',
              '&.Mui-focused': {
                display: 'none',
              },
            },
            '& .MuiInputBase-root': {
              '& .MuiInputBase-input': {
                height: value.length ? 0 : 22,
                py: '0px !important',
                pl: '4px !important',
                '&[aria-expanded="true"]': {
                  height: 22,
                },
              },
            },
            '& .MuiAutocomplete-inputRoot': {
              py: '10px !important',
              pl: '12px !important',
            },
            '& .MuiChip-root': {
              mt: '2px !important',
            },
          }}
        />
      )}
      filterOptions={filterOptions}
      renderOption={({ key, ...optionProps }, option) => {
        const {
          label: defaultLabel,
          primaryLabel,
          secondaryLabel,
          parent,
        } = option;

        const mainLabel = primaryLabel ?? defaultLabel;
        const isChild = Boolean(parent);

        return (
          <Stack
            key={key as string}
            {...optionProps}
            component="li"
            sx={{
              alignItems: 'center',
              flexDirection: 'row',
              height: 42,
              marginY: 0.25,
              paddingLeft: (t) => `${isChild ? t.spacing(3.5) : 0} !important`,
              '&.MuiAutocomplete-option[aria-selected="true"]': {
                backgroundColor: 'white !important',
              },
              '&.MuiAutocomplete-option[aria-selected="true"]:hover': {
                backgroundColor: (t) => `${t.palette.action.hover} !important`,
              },
            }}
          >
            <Checkbox {...getCheckboxState(option, selectedValues)} />

            <Typography
              sx={{
                marginRight: 2,
                color: isChild ? 'text.secondary' : undefined,
              }}
            >
              {mainLabel}
            </Typography>

            {secondaryLabel && (
              <Typography sx={{ color: 'text.secondary' }}>
                {secondaryLabel}
              </Typography>
            )}
          </Stack>
        );
      }}
      value={value}
    />
  );
}
